import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../components/AuthProvider";

/**
 * En qué punto está esta persona respecto a la invitación por correo
 * (captacion-devocional-y-boletin.md, "Según el estado de la persona"):
 *
 *   visitor     → invitación completa
 *   pending     → dejó el correo, falta el clic de confirmación (7 días)
 *   subscribed  → ya recibe el devocional (180 días, se renueva con cada visita desde un
 *                 correo nuestro)
 *   dismissed   → cerró la tarjeta flotante con "No, gracias" (30 días). Solo apaga la
 *                 tarjeta; los bloques dentro de la página siguen, porque no interrumpen.
 *
 * Sin cuenta, lo recuerda el navegador (localStorage). Solo se guarda el estado y la fecha,
 * NUNCA el correo — es la condición para que la marca no sea un dato personal. Con sesión
 * manda el espejo del perfil (`devotionalEmailStatus`), que escribe el servidor.
 *
 * Si la marca se pierde (otro dispositivo, datos borrados, Safari tras 7 días sin visitar) la
 * persona vuelve a ver la invitación, y el servidor le responde "ya estás suscrito". No se
 * rompe nada; por eso esto es una comodidad y no una fuente de verdad.
 */

const STORAGE_KEY = "vde_subscription";
const EVENT = "vde-subscription";
const DAY = 24 * 60 * 60 * 1000;
const TTL = { pending: 7 * DAY, subscribed: 180 * DAY, dismissed: 30 * DAY };

function readLocal() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return "visitor";
    const { state, at } = JSON.parse(raw);
    if (!TTL[state] || typeof at !== "number" || Date.now() - at > TTL[state]) return "visitor";
    return state;
  } catch (e) {
    return "visitor";
  }
}

export function setSubscriptionState(state) {
  try {
    if (state === "visitor") window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ state, at: Date.now() }));
  } catch (e) {
    // Almacenamiento bloqueado (modo privado, vista previa): la invitación sigue
    // funcionando, solo que no recuerda.
  }
  try {
    window.dispatchEvent(new Event(EVENT));
  } catch (e) {
    /* SSR o navegador sin Event */
  }
}

/**
 * Quien llega desde un link de un correo nuestro ya está inscrito: se renueva la marca. Los
 * correos del devocional (Fase 1) llevan `utm_medium=email`. Se llama desde Layout en cada
 * cambio de página.
 */
export function captureSubscriptionFromUrl(search) {
  try {
    const params = new URLSearchParams(search || "");
    if (params.get("utm_medium") === "email") setSubscriptionState("subscribed");
  } catch (e) {
    /* URL rara: se ignora */
  }
}

export function useSubscriptionState() {
  const { user, profile } = useAuth();
  // En SSR y en el primer render del cliente siempre 'visitor': el HTML estático lleva la
  // invitación completa (lo que ve la mayoría) y se ajusta al hidratar.
  const [local, setLocal] = useState("visitor");

  useEffect(() => {
    const sync = () => setLocal(readLocal());
    sync();
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  let state = local;
  if (user) {
    const mirror = profile?.devotionalEmailStatus;
    if (mirror === "confirmed") state = "subscribed";
    else if (mirror === "pending" && local !== "subscribed") state = "pending";
  }

  const set = useCallback((next) => setSubscriptionState(next), []);
  return [state, set];
}
